import React, { useState, useEffect } from 'react';
import { conceptExplanations } from './data/conceptExplanations';
import { domains, getDomainFromKey } from './data/domainStructure';
import ConceptModal from './components/ConceptModal';
import SearchBar from './components/SearchBar';
import Header from './components/Header';
import StudyTimer from './components/StudyTimer';
import DomainCard from './components/DomainCard';
import ScrollToTop from './components/ui/ScrollToTop';
import useBookmarks from './hooks/useBookmarks';
import useSearch from './hooks/useSearch';
import useStudyTimer from './hooks/useStudyTimer';
import { 
  initGA, 
  trackConceptViewed, 
  trackBookmarkToggle, 
  trackSearch, 
  trackDomainInteraction 
} from './utils/analytics';

const CompTIASecurityPlusObjectives = () => {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [viewedConcepts, setViewedConcepts] = useState(new Set());
  const [practiceMode, setPracticeMode] = useState(false);
  // Use custom hooks
  const {
    bookmarkedConcepts,
    showBookmarks,
    setShowBookmarks,
    toggleBookmark,
    getBookmarkedConcepts
  } = useBookmarks(getDomainFromKey);

  const {
    searchTerm,
    setSearchTerm,
    searchConcepts
  } = useSearch(getDomainFromKey);

  const {
    studyTime,
    isStudying,
    startStudySession,
    pauseStudySession,
    resetStudySession,
    getCurrentStudyTime,
    formatTime
  } = useStudyTimer(selectedConcept, selectedDomain, searchTerm);

  // Initialize Google Analytics on component mount
  useEffect(() => {
    initGA();
  }, []);

  // Progress tracking
  const ProgressTracker = () => {
    const totalConcepts = Object.keys(conceptExplanations).length;
    const viewedCount = viewedConcepts.size;
    const bookmarkedCount = bookmarkedConcepts.size;
    const progressPercentage = Math.round((viewedCount / totalConcepts) * 100);

    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-800">Study Progress</h3>
          <span className="text-sm font-medium text-gray-600">{viewedCount}/{totalConcepts} concepts</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
          <div 
            className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <span className="text-green-600">✓</span>
              <span className="text-gray-600">{viewedCount} studied</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-yellow-600">★</span>
              <span className="text-gray-600">{bookmarkedCount} bookmarked</span>
            </div>
          </div>
          <div className="text-gray-600">
            {progressPercentage}% • {practiceMode ? 'Practice Mode 🎯' : 'Study Mode 📚'}
          </div>
        </div>
      </div>
    );
  };

  // Study Timer Component

  // Practice Mode
  const PracticeMode = () => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);

    const practiceQuestions = Object.entries(conceptExplanations)
      .filter(([key, concept]) => concept && concept.title) // Only include valid concepts
      .map(([key, concept]) => ({
        question: `What is ${concept.title}?`,
        answer: concept.explanation || 'No explanation available',
        mnemonic: concept.mnemonic || '',
        example: concept.examples || 'No examples available'
      }));

    const nextQuestion = () => {
      if (currentQuestion < practiceQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setShowAnswer(false);
      }
    };

    const prevQuestion = () => {
      if (currentQuestion > 0) {
        setCurrentQuestion(currentQuestion - 1);
        setShowAnswer(false);
      }
    };

    if (!practiceMode) return null;

    return (
      <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-xl font-bold mb-4 text-yellow-800">🎯 Practice Mode</h3>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="mb-4">
            <span className="text-sm text-gray-500">Question {currentQuestion + 1} of {practiceQuestions.length}</span>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / practiceQuestions.length) * 100}%` }}
              ></div>
            </div>
          </div>
          <h4 className="text-lg font-semibold mb-3">{practiceQuestions[currentQuestion]?.question}</h4>
          {showAnswer ? (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <strong>Answer:</strong> {practiceQuestions[currentQuestion]?.answer}
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <strong>Memory Aid:</strong> {practiceQuestions[currentQuestion]?.mnemonic}
              </div>
              <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                <strong>Examples:</strong> {practiceQuestions[currentQuestion]?.example}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAnswer(true)}
              className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 transition-colors"
            >
              Show Answer
            </button>
          )}
          <div className="flex justify-between mt-4">
            <button
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
            >
              ← Previous
            </button>
            <button
              onClick={nextQuestion}
              disabled={currentQuestion === practiceQuestions.length - 1}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ClickableBullet = React.memo(({ children, conceptKey, level = 0 }) => {
    const handleClick = () => {
      if (conceptKey && conceptExplanations[conceptKey]) {
        setSelectedConcept(conceptExplanations[conceptKey]);
        setViewedConcepts(prev => new Set([...prev, conceptKey]));
        
        // Track concept view in Google Analytics
        trackConceptViewed(conceptKey, conceptExplanations[conceptKey].title);
      }
    };

    const isClickable = conceptKey && conceptExplanations[conceptKey];
    const isViewed = viewedConcepts.has(conceptKey);
    
    return (
      <li 
        className={`flex items-center justify-between ${isClickable ? 'cursor-pointer hover:bg-blue-50 hover:text-blue-700 rounded px-2 py-1 transition-colors' : ''} ${
          level > 0 ? `ml-${level * 4}` : ''
        } ${isViewed ? 'bg-green-50 border-l-2 border-green-400' : ''}`}
        onClick={handleClick}
        title={isClickable ? 'Click for detailed explanation' : ''}
      >
        <div className="flex-1">
          {children}
        </div>
        <div className="flex items-center space-x-1 ml-2">
          {isViewed && (
            <span className="text-green-600 text-sm" title="Concept studied">
              ✓
            </span>
          )}
          {bookmarkedConcepts.has(conceptKey) && (
            <span className="text-yellow-600 text-sm" title="Bookmarked">
              ★
            </span>
          )}
          {isClickable && (
            <span className="text-blue-500 text-xs" title="Click for details">
              ℹ️
            </span>
          )}
        </div>
      </li>
    );
  });


  const Domain1Content = () => (
    <div className="space-y-8">
      <div className="border-l-4 border-purple-600 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-purple-600 text-white px-2 py-1 rounded mr-3 text-sm">1.1</span>
          Compare and contrast various types of security controls.
        </h3>
        <div className="grid md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="categories">• Categories</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="technical">- Technical</ClickableBullet>
              <ClickableBullet conceptKey="managerial">- Managerial</ClickableBullet>
              <ClickableBullet conceptKey="operational">- Operational</ClickableBullet>
              <ClickableBullet conceptKey="physical">- Physical</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="control-types">• Control types</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="preventive">- Preventive</ClickableBullet>
              <ClickableBullet conceptKey="deterrent">- Deterrent</ClickableBullet>
              <ClickableBullet conceptKey="detective">- Detective</ClickableBullet>
              <ClickableBullet conceptKey="corrective">- Corrective</ClickableBullet>
              <ClickableBullet conceptKey="compensating">- Compensating</ClickableBullet>
              <ClickableBullet conceptKey="directive">- Directive</ClickableBullet>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-purple-600 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-purple-600 text-white px-2 py-1 rounded mr-3 text-sm">1.2</span>
          Summarize fundamental security concepts.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <ClickableBullet conceptKey="cia-triad">
              <h4 className="font-semibold mb-2">• Confidentiality, Integrity, and Availability (CIA)</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="non-repudiation">
              <h4 className="font-semibold mb-2">• Non-repudiation</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="aaa">
              <h4 className="font-semibold mb-2">• Authentication, Authorization, and Accounting (AAA)</h4>
            </ClickableBullet>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="authenticating-people">- Authenticating people</ClickableBullet>
              <ClickableBullet conceptKey="authenticating-systems">- Authenticating systems</ClickableBullet>
              <ClickableBullet conceptKey="authorization-models">- Authorization models</ClickableBullet>
            </ul>
            <ClickableBullet conceptKey="gap-analysis">
              <h4 className="font-semibold mb-2 mt-3">• Gap analysis</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="zero-trust">
              <h4 className="font-semibold mb-2">• Zero Trust</h4>
            </ClickableBullet>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="control-plane">- Control Plane</ClickableBullet>
              <ClickableBullet conceptKey="adaptive-identity" level={1}>° Adaptive identity</ClickableBullet>
              <ClickableBullet conceptKey="threat-scope-reduction" level={1}>° Threat scope reduction</ClickableBullet>
              <ClickableBullet conceptKey="policy-driven-access" level={1}>° Policy-driven access control</ClickableBullet>
              <ClickableBullet conceptKey="policy-administrator" level={1}>° Policy Administrator</ClickableBullet>
              <ClickableBullet conceptKey="policy-engine" level={1}>° Policy Engine</ClickableBullet>
              <ClickableBullet conceptKey="data-plane">- Data Plane</ClickableBullet>
              <ClickableBullet conceptKey="implicit-trust-zones" level={1}>° Implicit trust zones</ClickableBullet>
              <ClickableBullet conceptKey="subject-system" level={1}>° Subject/System</ClickableBullet>
              <ClickableBullet conceptKey="policy-enforcement-point" level={1}>° Policy Enforcement Point</ClickableBullet>
            </ul>
          </div>
          <div>
            <ClickableBullet conceptKey="physical-security">
              <h4 className="font-semibold mb-2">• Physical security</h4>
            </ClickableBullet>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="bollards">- Bollards</ClickableBullet>
              <ClickableBullet conceptKey="access-control-vestibule">- Access control vestibule</ClickableBullet>
              <ClickableBullet conceptKey="fencing">- Fencing</ClickableBullet>
              <ClickableBullet conceptKey="video-surveillance">- Video surveillance</ClickableBullet>
              <ClickableBullet conceptKey="security-guard">- Security guard</ClickableBullet>
              <ClickableBullet conceptKey="access-badge">- Access badge</ClickableBullet>
              <ClickableBullet conceptKey="lighting">- Lighting</ClickableBullet>
              <ClickableBullet conceptKey="sensors-physical">- Sensors</ClickableBullet>
              <ClickableBullet conceptKey="infrared-sensor" level={1}>° Infrared</ClickableBullet>
              <ClickableBullet conceptKey="pressure-sensor" level={1}>° Pressure</ClickableBullet>
              <ClickableBullet conceptKey="microwave-sensor" level={1}>° Microwave</ClickableBullet>
              <ClickableBullet conceptKey="ultrasonic-sensor" level={1}>° Ultrasonic</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="deception-disruption-technology">• Deception and disruption technology</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="honeypot">- Honeypot</ClickableBullet>
              <ClickableBullet conceptKey="honeynet">- Honeynet</ClickableBullet>
              <ClickableBullet conceptKey="honeyfile">- Honeyfile</ClickableBullet>
              <ClickableBullet conceptKey="honeytoken">- Honeytoken</ClickableBullet>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-purple-600 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-purple-600 text-white px-2 py-1 rounded mr-3 text-sm">1.3</span>
          Explain the importance of change management processes and the impact to security.
        </h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <ClickableBullet conceptKey="business-processes-security">
              <h4 className="font-semibold mb-2">• Business processes impacting security operation</h4>
            </ClickableBullet>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="approval-process">- Approval process</ClickableBullet>
              <ClickableBullet conceptKey="ownership">- Ownership</ClickableBullet>
              <ClickableBullet conceptKey="stakeholders">- Stakeholders</ClickableBullet>
              <ClickableBullet conceptKey="impact-analysis">- Impact analysis</ClickableBullet>
              <ClickableBullet conceptKey="test-results">- Test results</ClickableBullet>
              <ClickableBullet conceptKey="backout-plan">- Backout plan</ClickableBullet>
              <ClickableBullet conceptKey="maintenance-window">- Maintenance window</ClickableBullet>
              <ClickableBullet conceptKey="standard-operating-procedure">- Standard operating procedure</ClickableBullet>
            </ul>
          </div>
          <div>
            <ClickableBullet conceptKey="technical-implications">
              <h4 className="font-semibold mb-2">• Technical implications</h4>
            </ClickableBullet>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="allow-deny-lists">- Allow lists/deny lists</ClickableBullet>
              <ClickableBullet conceptKey="restricted-activities">- Restricted activities</ClickableBullet>
              <ClickableBullet conceptKey="downtime">- Downtime</ClickableBullet>
              <ClickableBullet conceptKey="service-restart">- Service restart</ClickableBullet>
              <ClickableBullet conceptKey="application-restart">- Application restart</ClickableBullet>
              <ClickableBullet conceptKey="legacy-applications">- Legacy applications</ClickableBullet>
              <ClickableBullet conceptKey="dependencies">- Dependencies</ClickableBullet>
            </ul>
          </div>
          <div>
            <ClickableBullet conceptKey="documentation">
              <h4 className="font-semibold mb-2">• Documentation</h4>
            </ClickableBullet>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="updating-diagrams">- Updating diagrams</ClickableBullet>
              <ClickableBullet conceptKey="updating-policies-procedures">- Updating policies/procedures</ClickableBullet>
            </ul>
            <ClickableBullet conceptKey="version-control">
              <h4 className="font-semibold mb-2 mt-3">• Version control</h4>
            </ClickableBullet>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-purple-600 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-purple-600 text-white px-2 py-1 rounded mr-3 text-sm">1.4</span>
          Explain the importance of using appropriate cryptographic solutions.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Public key infrastructure (PKI)</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="public-key">- Public key</ClickableBullet>
              <ClickableBullet conceptKey="private-key">- Private key</ClickableBullet>
              <ClickableBullet conceptKey="key-escrow">- Key escrow</ClickableBullet>
              <ClickableBullet conceptKey="certificate-authorities">- Certificate authorities</ClickableBullet>
              <ClickableBullet conceptKey="certificate-revocation-lists">- Certificate revocation lists (CRLs)</ClickableBullet>
              <ClickableBullet conceptKey="ocsp">- Online Certificate Status Protocol (OCSP)</ClickableBullet>
              <ClickableBullet conceptKey="self-signed-certificates">- Self-signed</ClickableBullet>
              <ClickableBullet conceptKey="third-party-certificates">- Third-party</ClickableBullet>
              <ClickableBullet conceptKey="root-of-trust">- Root of trust</ClickableBullet>
              <ClickableBullet conceptKey="csr-generation">- Certificate signing request (CSR) generation</ClickableBullet>
              <ClickableBullet conceptKey="wildcard-certificates">- Wildcard</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Encryption</h4>
            <ul className="ml-4 space-y-1">
              <li>- Level</li>
              <ClickableBullet conceptKey="full-disk-encryption" level={1}>° Full-disk</ClickableBullet>
              <ClickableBullet conceptKey="partition-encryption" level={1}>° Partition</ClickableBullet>
              <ClickableBullet conceptKey="file-encryption" level={1}>° File</ClickableBullet>
              <ClickableBullet conceptKey="volume-encryption" level={1}>° Volume</ClickableBullet>
              <ClickableBullet conceptKey="database-encryption" level={1}>° Database</ClickableBullet>
              <ClickableBullet conceptKey="record-encryption" level={1}>° Record</ClickableBullet>
              <ClickableBullet conceptKey="transport-communication-encryption">- Transport/communication</ClickableBullet>
              <ClickableBullet conceptKey="asymmetric-encryption">- Asymmetric</ClickableBullet>
              <ClickableBullet conceptKey="symmetric-encryption">- Symmetric</ClickableBullet>
              <ClickableBullet conceptKey="key-exchange">- Key exchange</ClickableBullet>
              <ClickableBullet conceptKey="algorithms">- Algorithms</ClickableBullet>
              <ClickableBullet conceptKey="key-length">- Key length</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Tools</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="tpm">- Trusted Platform Module (TPM)</ClickableBullet>
              <ClickableBullet conceptKey="hsm">- Hardware security module (HSM)</ClickableBullet>
              <ClickableBullet conceptKey="key-management-system">- Key management system</ClickableBullet>
              <ClickableBullet conceptKey="secure-enclave">- Secure enclave</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Obfuscation</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="steganography" level={1}>° Steganography</ClickableBullet>
              <ClickableBullet conceptKey="tokenization" level={1}>° Tokenization</ClickableBullet>
              <ClickableBullet conceptKey="data-masking" level={1}>° Data masking</ClickableBullet>
            </ul>
            <ClickableBullet conceptKey="hashing">
              <h4 className="font-semibold mb-2 mt-3">• Hashing</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="salting">
              <h4 className="font-semibold mb-2">• Salting</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="digital-signatures">
              <h4 className="font-semibold mb-2">• Digital signatures</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="key-stretching">
              <h4 className="font-semibold mb-2">• Key stretching</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="blockchain">
              <h4 className="font-semibold mb-2">• Blockchain</h4>
            </ClickableBullet>
            <ClickableBullet conceptKey="open-public-ledger">
              <h4 className="font-semibold mb-2">• Open public ledger</h4>
            </ClickableBullet>
          </div>
        </div>
      </div>
      
      {/* Next Domain Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => setSelectedDomain(2)}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors duration-200 flex items-center mx-auto"
        >
          Next: Domain 2 - Threats, Vulnerabilities, and Mitigations
          <span className="ml-2">→</span>
        </button>
      </div>
    </div>
  );

  const Domain2Content = () => (
    <div className="space-y-8">
      <div className="border-l-4 border-orange-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-sm">2.1</span>
          Compare and contrast common threat actors and motivations.
        </h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Threat actors</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="nation-state">- Nation-state</ClickableBullet>
              <ClickableBullet conceptKey="unskilled-attacker">- Unskilled attacker</ClickableBullet>
              <ClickableBullet conceptKey="hacktivist">- Hacktivist</ClickableBullet>
              <ClickableBullet conceptKey="insider-threat">- Insider threat</ClickableBullet>
              <ClickableBullet conceptKey="organized-crime">- Organized crime</ClickableBullet>
              <ClickableBullet conceptKey="shadow-it">- Shadow IT</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Attributes of actors</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="internal-external">- Internal/external</ClickableBullet>
              <ClickableBullet conceptKey="resources-funding">- Resources/funding</ClickableBullet>
              <ClickableBullet conceptKey="sophistication-capability">- Level of sophistication/capability</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Motivations</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="data-exfiltration">- Data exfiltration</ClickableBullet>
              <ClickableBullet conceptKey="espionage">- Espionage</ClickableBullet>
              <ClickableBullet conceptKey="service-disruption">- Service disruption</ClickableBullet>
              <ClickableBullet conceptKey="blackmail">- Blackmail</ClickableBullet>
              <ClickableBullet conceptKey="financial-gain">- Financial gain</ClickableBullet>
              <ClickableBullet conceptKey="philosophical-political-beliefs">- Philosophical/political beliefs</ClickableBullet>
              <ClickableBullet conceptKey="ethical">- Ethical</ClickableBullet>
              <ClickableBullet conceptKey="revenge">- Revenge</ClickableBullet>
              <ClickableBullet conceptKey="disruption-chaos">- Disruption/chaos</ClickableBullet>
              <ClickableBullet conceptKey="war">- War</ClickableBullet>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-orange-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-sm">2.2</span>
          Explain common threat vectors and attack surfaces.
        </h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Message-based</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="email">° Email</ClickableBullet>
              <ClickableBullet conceptKey="sms">° Short Message Service (SMS)</ClickableBullet>
              <ClickableBullet conceptKey="instant-messaging">° Instant messaging (IM)</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Image-based</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="embedded-malware">- Embedded malware</ClickableBullet>
              <ClickableBullet conceptKey="steganography-attacks">- Steganography attacks</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2">• File-based</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="portable-executable">- Portable executable</ClickableBullet>
              <ClickableBullet conceptKey="office-documents">- Office documents</ClickableBullet>
              <ClickableBullet conceptKey="pdf-attacks">- PDF attacks</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2">• Voice call</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="voice-call-threats">- Voice call threats</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2">• Removable device</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="removable-device-attacks">- Removable device attacks</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2">• Vulnerable software</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="vulnerable-software">- Vulnerable software components</ClickableBullet>
              <ClickableBullet conceptKey="client-based-agentless" level={1}>° Client-based vs. agentless</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Unsupported systems and applications</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="unsupported-systems">- Unsupported systems and applications</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Unsecure networks</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="unsecure-networks">- Unsecure networks</ClickableBullet>
              <ClickableBullet conceptKey="wireless-networks" level={1}>- Wireless</ClickableBullet>
              <ClickableBullet conceptKey="wired-networks" level={1}>- Wired</ClickableBullet>
              <ClickableBullet conceptKey="bluetooth-threats" level={1}>- Bluetooth</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Open service ports</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="open-service-ports">- Open service ports</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2">• Default credentials</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="default-credentials">- Default credentials</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2">• Supply chain</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="supply-chain-attacks">- Supply chain attacks</ClickableBullet>
              <ClickableBullet conceptKey="msps-vendors-suppliers" level={1}>- Managed service providers (MSPs)</ClickableBullet>
              <ClickableBullet conceptKey="msps-vendors-suppliers" level={1}>- Vendors</ClickableBullet>
              <ClickableBullet conceptKey="msps-vendors-suppliers" level={1}>- Suppliers</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Human vectors/social engineering</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="phishing">- Phishing</ClickableBullet>
              <ClickableBullet conceptKey="vishing">- Vishing</ClickableBullet>
              <ClickableBullet conceptKey="smishing">- Smishing</ClickableBullet>
              <ClickableBullet conceptKey="misinformation">- Misinformation/disinformation</ClickableBullet>
              <ClickableBullet conceptKey="impersonation">- Impersonation</ClickableBullet>
              <ClickableBullet conceptKey="business-email-compromise">- Business email compromise</ClickableBullet>
              <ClickableBullet conceptKey="pretexting">- Pretexting</ClickableBullet>
              <ClickableBullet conceptKey="watering-hole">- Watering hole</ClickableBullet>
              <ClickableBullet conceptKey="brand-impersonation">- Brand impersonation</ClickableBullet>
              <ClickableBullet conceptKey="typosquatting">- Typosquatting</ClickableBullet>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-orange-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-sm">2.3</span>
          Explain various types of vulnerabilities.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="application-vulnerabilities">• Application</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="memory-injection">- Memory injection</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="buffer-overflow">- Buffer overflow</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="race-conditions">- Race conditions</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="time-of-check-toc">° Time-of-check (TOC)</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="time-of-use-tou">° Time-of-use (TOU)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="malicious-update">- Malicious update</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="os-based-vulnerabilities">• Operating system (OS)-based</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="web-based-vulnerabilities">• Web-based</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="sql-injection">- Structured Query Language injection (SQLi)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="cross-site-scripting">- Cross-site scripting (XSS)</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="hardware-vulnerabilities">• Hardware</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="firmware-vulnerabilities">- Firmware</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="end-of-life-vulnerabilities">- End-of-life</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="legacy-vulnerabilities">- Legacy</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="virtualization-vulnerabilities">• Virtualization</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="vm-escape">- Virtual machine (VM) escape</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="resource-reuse">- Resource reuse</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="cloud-specific-vulnerabilities">• Cloud-specific</ClickableBullet>
            </h4>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="supply-chain-vulnerabilities">• Supply chain</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="service-provider-vulnerabilities">- Service provider</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardware-provider-vulnerabilities">- Hardware provider</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="software-provider-vulnerabilities">- Software provider</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="cryptographic-vulnerabilities">• Cryptographic</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="misconfiguration-vulnerabilities">• Misconfiguration</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="mobile-device-vulnerabilities">• Mobile device</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="side-loading">- Side loading</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="jailbreaking">- Jailbreaking</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="zero-day-vulnerabilities">• Zero-day</ClickableBullet>
            </h4>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-orange-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-sm">2.4</span>
          Given a scenario, analyze indicators of malicious activity.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Malware attacks</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="ransomware">- Ransomware</ClickableBullet>
              <ClickableBullet conceptKey="trojan">- Trojan</ClickableBullet>
              <ClickableBullet conceptKey="worm">- Worm</ClickableBullet>
              <ClickableBullet conceptKey="spyware">- Spyware</ClickableBullet>
              <ClickableBullet conceptKey="bloatware">- Bloatware</ClickableBullet>
              <ClickableBullet conceptKey="virus">- Virus</ClickableBullet>
              <ClickableBullet conceptKey="keylogger">- Keylogger</ClickableBullet>
              <ClickableBullet conceptKey="logic-bomb">- Logic bomb</ClickableBullet>
              <ClickableBullet conceptKey="rootkit">- Rootkit</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Physical attacks</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="brute-force-physical">- Brute force</ClickableBullet>
              <ClickableBullet conceptKey="rfid-cloning">- Radio frequency identification (RFID) cloning</ClickableBullet>
              <ClickableBullet conceptKey="environmental-attacks">- Environmental</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Network attacks</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="ddos-attacks">- Distributed denial-of-service (DDoS)</ClickableBullet>
              <ClickableBullet conceptKey="amplified-ddos" level={1}>° Amplified</ClickableBullet>
              <ClickableBullet conceptKey="reflected-ddos" level={1}>° Reflected</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Application attacks</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="injection-attacks">- Injection</ClickableBullet>
              <ClickableBullet conceptKey="buffer-overflow">- Buffer overflow</ClickableBullet>
              <ClickableBullet conceptKey="replay-attacks">- Replay</ClickableBullet>
              <ClickableBullet conceptKey="privilege-escalation">- Privilege escalation</ClickableBullet>
              <ClickableBullet conceptKey="forgery-attacks">- Forgery</ClickableBullet>
              <ClickableBullet conceptKey="directory-traversal">- Directory traversal</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Indicators</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="account-lockout">- Account lockout</ClickableBullet>
              <ClickableBullet conceptKey="concurrent-sessions">- Concurrent session usage</ClickableBullet>
              <ClickableBullet conceptKey="blocked-content">- Blocked content</ClickableBullet>
              <ClickableBullet conceptKey="impossible-travel">- Impossible travel</ClickableBullet>
              <ClickableBullet conceptKey="resource-consumption">- Resource consumption</ClickableBullet>
              <ClickableBullet conceptKey="resource-inaccessibility">- Resource inaccessibility</ClickableBullet>
              <ClickableBullet conceptKey="out-of-cycle-logging">- Out-of-cycle logging</ClickableBullet>
              <ClickableBullet conceptKey="published-documented-vulnerabilities">- Published/documented</ClickableBullet>
              <ClickableBullet conceptKey="missing-logs">- Missing logs</ClickableBullet>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-orange-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-sm">2.5</span>
          Explain the purpose of mitigation techniques used to secure the enterprise.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="segmentation-mitigation">• Segmentation</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="access-control-mitigation">• Access control</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="access-control-list-mitigation">- Access control list (ACL)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="permissions-mitigation">- Permissions</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="application-allow-list">• Application allow list</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="isolation-mitigation">• Isolation</ClickableBullet>
            </h4>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="patching-mitigation">• Patching</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="encryption-mitigation">• Encryption</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="monitoring-mitigation">• Monitoring</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="least-privilege-mitigation">• Least privilege</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="configuration-enforcement">• Configuration enforcement</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="decommissioning-mitigation">• Decommissioning</ClickableBullet>
            </h4>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="hardening-techniques-mitigation">• Hardening techniques</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="encryption-hardening">- Encryption</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="endpoint-protection-installation">- Installation of endpoint protection</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="host-based-firewall">- Host-based firewall</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="host-based-ips">- Host-based intrusion prevention system (HIPS)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="disabling-ports-protocols">- Disabling ports/protocols</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="default-password-changes">- Default password changes</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="removal-unnecessary-software">- Removal of unnecessary software</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Next Domain Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => setSelectedDomain(3)}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors duration-200 flex items-center mx-auto"
        >
          Next: Domain 3 - Security Architecture
          <span className="ml-2">→</span>
        </button>
      </div>
    </div>
  );

  const DomainNavigation = () => (
    <div className="mb-8">
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setSelectedDomain(null)}
          className={`px-4 py-2 rounded-lg transition-colors ${
            selectedDomain === null 
              ? 'bg-gray-800 text-white' 
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          All Domains
        </button>
        {domains.map((domain) => (
          <button
            key={domain.id}
            onClick={() => setSelectedDomain(domain.id)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedDomain === domain.id 
                ? domain.color + ' text-white' 
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Domain {domain.id}
          </button>
        ))}
      </div>
      {selectedDomain && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <p className="text-sm text-blue-700">
            💡 Click on any item with an ℹ️ icon to see detailed explanations and memory aids!
          </p>
        </div>
      )}
    </div>
  );

  const Domain3Content = () => (
    <div className="space-y-8">
      <div className="border-l-4 border-blue-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-blue-500 text-white px-2 py-1 rounded mr-3 text-sm">3.1</span>
          Compare and contrast security implications of different architecture models.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Architecture and infrastructure concepts</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="cloud-architecture">- Cloud</ClickableBullet>
              <ClickableBullet conceptKey="cloud-responsibility" level={1}>° Responsibility matrix</ClickableBullet>
              <ClickableBullet conceptKey="hybrid-considerations" level={1}>° Hybrid considerations</ClickableBullet>
              <ClickableBullet conceptKey="third-party-vendors" level={1}>° Third-party vendors</ClickableBullet>
              <ClickableBullet conceptKey="infrastructure-as-code">- Infrastructure as code (IaC)</ClickableBullet>
              <ClickableBullet conceptKey="serverless-architecture">- Serverless</ClickableBullet>
              <ClickableBullet conceptKey="microservices">- Microservices</ClickableBullet>
              <li>- Network infrastructure</li>
              <ClickableBullet conceptKey="air-gapped-isolation" level={1}>° Physical isolation (Air-gapped)</ClickableBullet>
              <ClickableBullet conceptKey="logical-segmentation" level={1}>° Logical segmentation</ClickableBullet>
              <ClickableBullet conceptKey="software-defined-networking" level={1}>° Software-defined networking (SDN)</ClickableBullet>
            </ul>
          </div>
          <div>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="on-premises-architecture">- On-premises</ClickableBullet>
              <ClickableBullet conceptKey="centralized-decentralized">- Centralized vs. decentralized</ClickableBullet>
              <ClickableBullet conceptKey="containerization">- Containerization</ClickableBullet>
              <ClickableBullet conceptKey="virtualization">- Virtualization</ClickableBullet>
              <ClickableBullet conceptKey="iot-architecture">- IoT</ClickableBullet>
              <ClickableBullet conceptKey="ics-scada">- Industrial control systems (ICS)/SCADA</ClickableBullet>
              <ClickableBullet conceptKey="rtos">- Real-time operating system (RTOS)</ClickableBullet>
              <ClickableBullet conceptKey="embedded-systems">- Embedded systems</ClickableBullet>
              <ClickableBullet conceptKey="high-availability">- High availability</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Considerations</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="availability-consideration">- Availability</ClickableBullet>
              <ClickableBullet conceptKey="resilience-consideration">- Resilience</ClickableBullet>
              <ClickableBullet conceptKey="cost-consideration">- Cost</ClickableBullet>
              <ClickableBullet conceptKey="responsiveness-consideration">- Responsiveness</ClickableBullet>
              <ClickableBullet conceptKey="scalability-consideration">- Scalability</ClickableBullet>
              <ClickableBullet conceptKey="ease-of-deployment">- Ease of deployment</ClickableBullet>
              <ClickableBullet conceptKey="risk-transference">- Risk transference</ClickableBullet>
              <ClickableBullet conceptKey="ease-of-recovery">- Ease of recovery</ClickableBullet>
              <ClickableBullet conceptKey="patch-availability">- Patch availability</ClickableBullet>
              <ClickableBullet conceptKey="inability-to-patch">- Inability to patch</ClickableBullet>
              <ClickableBullet conceptKey="power-consideration">- Power</ClickableBullet>
              <ClickableBullet conceptKey="compute-consideration">- Compute</ClickableBullet>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-blue-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-blue-500 text-white px-2 py-1 rounded mr-3 text-sm">3.2</span>
          Given a scenario, apply security principles to secure enterprise infrastructure.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Infrastructure considerations</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="device-placement">- Device placement</ClickableBullet>
              <ClickableBullet conceptKey="security-zones">- Security zones</ClickableBullet>
              <ClickableBullet conceptKey="attack-surface">- Attack surface</ClickableBullet>
              <ClickableBullet conceptKey="connectivity">- Connectivity</ClickableBullet>
              <li>- Failure modes</li>
              <ClickableBullet conceptKey="fail-open-closed" level={1}>° Fail-open/Fail-closed</ClickableBullet>
              <li>- Device attribute</li>
              <ClickableBullet conceptKey="active-passive-monitoring" level={1}>° Active vs. passive</ClickableBullet>
              <ClickableBullet conceptKey="inline-tap-monitoring" level={1}>° Inline vs. tap/monitor</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Network appliances</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="jump-server">- Jump server</ClickableBullet>
              <ClickableBullet conceptKey="proxy-server">- Proxy server</ClickableBullet>
              <ClickableBullet conceptKey="ips-ids-systems">- IPS/IDS</ClickableBullet>
              <ClickableBullet conceptKey="load-balancer">- Load balancer</ClickableBullet>
              <ClickableBullet conceptKey="network-sensors">- Sensors</ClickableBullet>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Port security</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="802.1x">- 802.1X</ClickableBullet>
              <ClickableBullet conceptKey="eap">- Extensible Authentication Protocol (EAP)</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Firewall types</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="waf">- Web application firewall (WAF)</ClickableBullet>
              <ClickableBullet conceptKey="utm">- Unified threat management (UTM)</ClickableBullet>
              <ClickableBullet conceptKey="ngfw">- Next-generation firewall (NGFW)</ClickableBullet>
              <ClickableBullet conceptKey="layer4-layer7">- Layer 4/Layer 7</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Secure communication/access</h4>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="vpn">- Virtual private network (VPN)</ClickableBullet>
              <ClickableBullet conceptKey="remote-access">- Remote access</ClickableBullet>
              <ClickableBullet conceptKey="tunneling">- Tunneling</ClickableBullet>
              <ClickableBullet conceptKey="tls" level={1}>° Transport Layer Security (TLS)</ClickableBullet>
              <ClickableBullet conceptKey="ipsec" level={1}>° Internet protocol security (IPSec)</ClickableBullet>
            </ul>
          </div>
          <div>
            <ul className="ml-4 space-y-1">
              <ClickableBullet conceptKey="sd-wan">- Software-defined wide area network (SD-WAN)</ClickableBullet>
              <ClickableBullet conceptKey="sase">- Secure access service edge (SASE)</ClickableBullet>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Selection of effective controls</h4>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-blue-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-blue-500 text-white px-2 py-1 rounded mr-3 text-sm">3.3</span>
          Compare and contrast concepts and strategies to protect data.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="data-types">• Data types</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="regulated-data">- Regulated</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="trade-secret">- Trade secret</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="intellectual-property">- Intellectual property</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="legal-information">- Legal information</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="financial-information">- Financial information</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="human-non-human-readable">- Human- and non-human-readable</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="data-classifications">• Data classifications</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="sensitive-data">- Sensitive</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="confidential-data">- Confidential</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="public-data">- Public</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="restricted-data">- Restricted</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="private-data">- Private</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="critical-data">- Critical</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="general-data-considerations">• General data considerations</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="data-states">- Data states</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="data-at-rest" level={1}>° Data at rest</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="data-in-transit" level={1}>° Data in transit</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="data-in-use" level={1}>° Data in use</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-sovereignty">- Data sovereignty</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="geolocation-data">- Geolocation</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="methods-secure-data">• Methods to secure data</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="geographic-restrictions">- Geographic restrictions</ClickableBullet>
              </li>
              <li>- Encryption</li>
              <li>- Hashing</li>
              <li>
                <ClickableBullet conceptKey="data-masking">- Masking</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="tokenization">- Tokenization</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="obfuscation">- Obfuscation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="network-segmentation">- Segmentation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="permission-restrictions">- Permission restrictions</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-blue-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-blue-500 text-white px-2 py-1 rounded mr-3 text-sm">3.4</span>
          Explain the importance of resilience and recovery in security architecture.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="high-availability">• High availability</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="load-balancing">- Load balancing</ClickableBullet> vs. <ClickableBullet conceptKey="clustering">clustering</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Site considerations</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="hot-site">- Hot</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="cold-site">- Cold</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="warm-site">- Warm</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="geographic-dispersion">- Geographic dispersion</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="platform-diversity">• Platform diversity</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="multi-cloud-systems">• Multi-cloud systems</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="continuity-of-operations">• Continuity of operations</ClickableBullet>
            </h4>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="capacity-planning">• Capacity planning</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="people-capacity">- People</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="technology-capacity">- Technology</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="infrastructure-capacity">- Infrastructure</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Testing</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="tabletop-exercises">- Tabletop exercises</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="failover-testing">- Fail over</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="simulation-testing">- Simulation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="parallel-processing">- Parallel processing</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Backups</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="backup-onsite-offsite">- Onsite/offsite</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="backup-frequency">- Frequency</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="backup-encryption">- Encryption</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="backup-snapshots">- Snapshots</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="backup-recovery">- Recovery</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="backup-replication">- Replication</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="backup-journaling">- Journaling</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Power</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="power-generators">- Generators</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ups-power">- Uninterruptible power supply (UPS)</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Next Domain Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => setSelectedDomain(4)}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors duration-200 flex items-center mx-auto"
        >
          Next: Domain 4 - Security Operations
          <span className="ml-2">→</span>
        </button>
      </div>
    </div>
  );

  const Domain4Content = () => (
    <div className="space-y-8">
      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.1</span>
          Given a scenario, apply common security techniques to computing resources.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="secure-baselines">• Secure baselines</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="secure-baselines-establish">- Establish</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="secure-baselines-deploy">- Deploy</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="secure-baselines-maintain">- Maintain</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="hardening-targets">• Hardening targets</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="hardening-mobile-devices">- Mobile devices</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-workstations">- Workstations</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-switches">- Switches</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-routers">- Routers</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-cloud-infrastructure">- Cloud infrastructure</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-servers">- Servers</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-ics-scada">- ICS/SCADA</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-embedded-systems">- Embedded systems</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-rtos">- RTOS</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hardening-iot-devices">- IoT devices</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="wireless-devices">• Wireless devices</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="wireless-installation-considerations">- Installation considerations</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="site-surveys">° Site surveys</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="heat-maps">° Heat maps</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Mobile solutions</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="mdm">- Mobile device management (MDM)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="mobile-deployment-models">- Deployment models</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="byod">° Bring your own device (BYOD)</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="cope">° Corporate-owned, personally enabled (COPE)</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="cyod">° Choose your own device (CYOD)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="mobile-connection-methods">- Connection methods</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="cellular-connection">° Cellular</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="wifi-connection">° Wi-Fi</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="bluetooth-connection">° Bluetooth</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="wireless-security-settings">• Wireless security settings</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="wpa3">- Wi-Fi Protected Access 3 (WPA3)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="aaa-radius">- AAA/Remote Authentication Dial-In User Service (RADIUS)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="cryptographic-protocols">- Cryptographic protocols</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="authentication-protocols">- Authentication protocols</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="application-security">• Application security</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="input-validation">- Input validation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="secure-cookies">- Secure cookies</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="static-code-analysis">- Static code analysis</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="code-signing">- Code signing</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="sandboxing">• Sandboxing</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="security-monitoring">• Monitoring</ClickableBullet>
            </h4>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.2</span>
          Explain the security implications of proper hardware, software, and data asset management.
        </h3>
        <div className="grid md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="acquisition-procurement">• Acquisition/procurement process</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="assignment-accounting">• Assignment/accounting</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="asset-ownership">- Ownership</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="asset-classification">- Classification</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="monitoring-asset-tracking">• Monitoring/asset tracking</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="asset-inventory">- Inventory</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="asset-enumeration">- Enumeration</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="disposal-decommissioning">• Disposal/decommissioning</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="asset-sanitization">- Sanitization</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="asset-destruction">- Destruction</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="destruction-certification">- Certification</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-retention">- Data retention</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.3</span>
          Explain various activities associated with vulnerability management.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">• Identification methods</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="vulnerability-scanning">- Vulnerability scan</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="application-security-testing">- Application security</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="static-analysis" level={1}>° Static analysis</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="dynamic-analysis" level={1}>° Dynamic analysis</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="package-monitoring" level={1}>° Package monitoring</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="threat-feeds">- Threat feed</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="osint" level={1}>° Open-source intelligence (OSINT)</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="proprietary-threat-intel" level={1}>° Proprietary/third-party</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="information-sharing-org" level={1}>° Information-sharing organization</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="dark-web-monitoring" level={1}>° Dark web</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="penetration-testing-vendor">- Penetration testing</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="responsible-disclosure">- Responsible disclosure program</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="bug-bounty-program" level={1}>° Bug bounty program</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="system-process-audit">- System/process audit</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">• Analysis</h4>
            <ul className="ml-4 space-y-1">
              <li>- Confirmation</li>
              <li className="ml-4">
                <ClickableBullet conceptKey="false-positive-vuln" level={1}>° False positive</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="false-negative-vuln" level={1}>° False negative</ClickableBullet>
              </li>
              <li>- Prioritize</li>
              <li>
                <ClickableBullet conceptKey="cvss-scoring">- Common Vulnerability Scoring System (CVSS)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="cve-enumeration">- Common Vulnerability Enumeration (CVE)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="vulnerability-classification">- Vulnerability classification</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="exposure-factor-risk">- Exposure factor</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="environmental-variables">- Environmental variables</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="industry-organizational-impact">- Industry/organizational impact</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="risk-tolerance">- Risk tolerance</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="vulnerability-response-remediation">• Vulnerability response and remediation</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="patching">- Patching</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="insurance">- Insurance</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="segmentation">- Segmentation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="compensating-controls">- Compensating controls</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="exceptions-exemptions">- Exceptions and exemptions</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="validation-remediation">• Validation of remediation</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="rescanning">- Rescanning</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="audit">- Audit</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="verification">- Verification</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="reporting">• Reporting</ClickableBullet>
            </h4>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.4</span>
          Explain security alerting and monitoring concepts and tools.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="monitoring-computing-resources">• Monitoring computing resources</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="monitoring-systems">- Systems</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="monitoring-applications">- Applications</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="monitoring-infrastructure">- Infrastructure</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="monitoring-activities">• Activities</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="log-aggregation">- Log aggregation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="alerting">- Alerting</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="scanning">- Scanning</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="security-reporting">- Reporting</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="archiving">- Archiving</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="alert-response-remediation">- Alert response and remediation/validation</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="quarantine" level={1}>° Quarantine</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="alert-tuning" level={1}>° Alert tuning</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="security-monitoring-tools">• Tools</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="scap">- Security Content Automation Protocol (SCAP)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="benchmarks">- Benchmarks</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="agents-agentless">- Agents/agentless</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="siem-tools">- Security information and event management (SIEM)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="antivirus-monitoring">- Antivirus</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="dlp-monitoring">- Data loss prevention (DLP)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="snmp-traps">- Simple Network Management Protocol (SNMP) traps</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="netflow-monitoring">- NetFlow</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="vulnerability-scanner-monitoring">- Vulnerability scanners</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.5</span>
          Explain the key aspects of digital forensics.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="documentation-evidence">• Documentation/evidence</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="legal-hold">- Legal hold</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="video-evidence">- Video</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="admissibility">- Admissibility</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="chain-of-custody">- Chain of custody</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="timelines">- Timelines</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="tags">- Tags</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="reports">- Reports</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="event-logs">- Event logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="interviews">- Interviews</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="acquisition">• Acquisition</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="order-of-volatility">- Order of volatility</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="disk-acquisition">- Disk</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ram-acquisition">- Random-access memory (RAM)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="swap-pagefile-acquisition">- Swap/pagefile</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="os-acquisition">- OS</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="device-acquisition">- Device</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="firmware-acquisition">- Firmware</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="snapshot-acquisition">- Snapshot</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="cache-acquisition">- Cache</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="network-acquisition">- Network</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="artifacts-acquisition">- Artifacts</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="on-premises-vs-cloud">• On-premises vs. cloud</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="right-to-audit">- Right-to-audit clauses</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="regulatory-jurisdiction">- Regulatory/jurisdiction</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-breach-notification">- Data breach notification laws</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="integrity">• Integrity</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="forensic-hashing">- Hashing</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="forensic-checksums">- Checksums</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="provenance">- Provenance</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="preservation">• Preservation</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="e-discovery">• E-discovery</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="data-recovery">• Data recovery</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="non-repudiation">• Non-repudiation</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="strategic-intelligence">• Strategic intelligence/counterintelligence</ClickableBullet>
            </h4>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.6</span>
          Given a scenario, implement security automation and orchestration.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="automation-concepts">• Automation concepts</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="security-automation">- Security automation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="resource-provisioning">- Resource provisioning</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="configuration-management">- Configuration management</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="policy-enforcement">- Policy enforcement</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="soar">- SOAR (Security Orchestration, Automation, and Response)</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="use-cases">• Use cases</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="incident-response-automation">- Incident response automation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="threat-hunting-automation">- Threat hunting automation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="compliance-reporting">- Compliance reporting</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="user-provisioning">- User provisioning</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="resource-provisioning-automation">- Resource provisioning</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="benefits">• Benefits</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="automation-efficiency">- Efficiency</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="automation-consistency">- Consistency</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="automation-scalability">- Scalability</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="response-time-improvement">- Response time improvement</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="error-reduction">- Error reduction</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="other-considerations">• Other considerations</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="automation-complexity">- Complexity</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="automation-cost">- Cost</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="single-point-failure">- Single point of failure</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="technical-debt">- Technical debt</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ongoing-supportability">- Ongoing supportability</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="integration-apis">• Integration and APIs</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="rest-api">- REST API</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="soap">- SOAP</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="json">- JSON</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="xml">- XML</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="webhooks">- Webhooks</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="protocols-standards">• Automation protocols and standards</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="scap-protocol">- Security Content Automation Protocol (SCAP)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="oval">- Open Vulnerability and Assessment Language (OVAL)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="stix-taxii">- STIX/TAXII</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.7</span>
          Given a scenario, use data sources to support an investigation.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="log-data">• Log data</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="firewall-logs">- Firewall logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="application-logs">- Application logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="endpoint-logs">- Endpoint logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="os-security-logs">- OS-specific security logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ips-ids-logs">- IPS/IDS logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="network-logs">- Network logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="metadata-investigation">- Metadata</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="data-sources-investigation">• Data sources</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="vulnerability-scans-investigation">- Vulnerability scans</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="automated-reports">- Automated reports</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="dashboards-investigation">- Dashboards</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="packet-captures">- Packet captures</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="analysis-investigation">• Analysis</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="correlation">- Correlation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="trend-analysis">- Trend analysis</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="statistical-analysis">- Statistical analysis</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="behavioral-analysis">- Behavioral analysis</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="baseline-deviation">- Baseline deviation</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="siem-dashboards">• SIEM review</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="rule-writing">- Rule writing</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="known-bad-indicators">- Known bad indicators</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="unknown-suspicious-activity">- Unknown/suspicious activity</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="user-behavior-analytics">• User behavior analytics</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="sentiment-analysis">• Sentiment analysis</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="communication-analysis">• Communication analysis</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="threat-feeds-analysis">- Threat feeds</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="advisories">- Advisories</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="bulletins">- Bulletins</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="data-sensitivity-classification">• Data sensitivity and classification</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="security-monitoring-investigation">• Security monitoring</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="flow-analysis">- Flow analysis</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="packet-analysis">- Packet analysis</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="protocol-analysis">- Protocol analysis</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.8</span>
          Explain appropriate incident response activities.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="ir-process">• Process</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="incident-response-steps">- Preparation, Detection, Analysis, Containment, Eradication, Recovery, Lessons learned</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="ir-training">• Training</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="ir-testing">• Testing</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="tabletop-exercise">- Tabletop exercise</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="simulation">- Simulation</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="ir-root-cause-analysis">• Root cause analysis</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="ir-threat-hunting">• Threat hunting</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="ir-digital-forensics">• Digital forensics</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="legal-hold">- Legal hold</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="chain-of-custody">- Chain of custody</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="acquisition">- Acquisition</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="reporting-forensics">- Reporting</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="preservation">- Preservation</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="e-discovery">- E-discovery</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-yellow-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-yellow-500 text-white px-2 py-1 rounded mr-3 text-sm">4.9</span>
          Given a scenario, use data sources to support an investigation.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="log-data-investigation">• Log data</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="firewall-logs-investigation">- Firewall logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="application-logs-investigation">- Application logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="endpoint-logs-investigation">- Endpoint logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="os-security-logs-investigation">- OS-specific security logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ips-ids-logs-investigation">- IPS/IDS logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="network-logs-investigation">- Network logs</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="metadata-investigation">- Metadata</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="data-sources-investigation">• Data sources</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="vulnerability-scans-investigation">- Vulnerability scans</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="automated-reports-investigation">- Automated reports</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="dashboards-investigation">- Dashboards</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="packet-captures-investigation">- Packet captures</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Next Domain Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => setSelectedDomain(5)}
          className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-colors duration-200 flex items-center mx-auto"
        >
          Next: Domain 5 - Security Program Management
          <span className="ml-2">→</span>
        </button>
      </div>
    </div>
  );

  const Domain5Content = () => (
    <div className="space-y-8">
      <div className="border-l-4 border-green-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-green-500 text-white px-2 py-1 rounded mr-3 text-sm">5.1</span>
          Summarize elements of effective security governance.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="security-guidelines">• Guidelines</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">• Policies</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="aup">- Acceptable use policy (AUP)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="information-security-policies">- Information security policies</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="business-continuity-policy">- Business continuity</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="disaster-recovery-policy">- Disaster recovery</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="incident-response-policy">- Incident response</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="sdlc-policy">- Software development lifecycle (SDLC)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="change-management-policy">- Change management</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">• Standards</h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="password-standards">- Password</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="access-control-standards">- Access control</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="physical-security-standards">- Physical security</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="encryption-standards">- Encryption</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="procedures">• Procedures</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="change-management">- Change management</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="onboarding-offboarding">- Onboarding/offboarding</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="playbooks">- Playbooks</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="external-considerations">• External considerations</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="regulatory">- Regulatory</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="legal">- Legal</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="industry">- Industry</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="local-regional">- Local/regional</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="national">- National</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="global">- Global</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="monitoring-revision">• Monitoring and revision</ClickableBullet>
            </h4>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="governance-structures">• Types of governance structures</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="boards">- Boards</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="committees">- Committees</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="government-entities">- Government entities</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="centralized-decentralized-governance">- Centralized/decentralized</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="roles-responsibilities">• Roles and responsibilities for systems and data</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="data-owners">- Owners</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-controllers">- Controllers</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-processors">- Processors</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="custodians-stewards">- Custodians/stewards</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-green-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-green-500 text-white px-2 py-1 rounded mr-3 text-sm">5.2</span>
          Explain elements of the risk management process.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="risk-identification">• Risk identification</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="risk-assessment">• Risk assessment</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="ad-hoc">- Ad hoc</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="recurring">- Recurring</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="one-time">- One-time</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="continuous">- Continuous</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="risk-analysis">• Risk analysis</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="qualitative">- Qualitative</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="quantitative">- Quantitative</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="sle">- Single loss expectancy (SLE)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ale">- Annualized loss expectancy (ALE)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="aro">- Annualized rate of occurrence (ARO)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="probability">- Probability</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="likelihood">- Likelihood</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="exposure-factor-risk">- Exposure factor</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="impact">- Impact</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="risk-register">• Risk register</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="key-risk-indicators">- Key risk indicators</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="risk-owners">- Risk owners</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="risk-threshold">- Risk threshold</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="risk-tolerance">• Risk tolerance</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="risk-appetite">• Risk appetite</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="expansionary">- Expansionary</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="conservative">- Conservative</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="neutral">- Neutral</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="risk-management">• Risk management strategies</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="transfer">- Transfer</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="accept">- Accept</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="exemption" level={1}>° Exemption</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="exception" level={1}>° Exception</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="avoid">- Avoid</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="mitigate">- Mitigate</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="risk-reporting">• Risk reporting</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="business-impact-analysis">• Business impact analysis</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="rto">- Recovery time objective (RTO)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="rpo">- Recovery point objective (RPO)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="mttr">- Mean time to repair (MTTR)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="mtbf">- Mean time between failures (MTBF)</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-green-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-green-500 text-white px-2 py-1 rounded mr-3 text-sm">5.3</span>
          Explain the processes associated with third-party risk assessment and management.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="vendor-assessment">• Vendor assessment</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="penetration-testing-vendor">- Penetration testing</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="right-to-audit-clause">- Right-to-audit clause</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="evidence-internal-audits">- Evidence of internal audits</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="independent-assessments">- Independent assessments</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="supply-chain-analysis">- Supply chain analysis</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="vendor-selection">• Vendor selection</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="due-diligence-third-party">- Due diligence</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="conflict-of-interest">- Conflict of interest</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="agreement-types">• Agreement types</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="sla">- Service-level agreement (SLA)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="moa">- Memorandum of agreement (MOA)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="mou">- Memorandum of understanding (MOU)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="msa">- Master service agreement (MSA)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="sow">- Work order (WO)/statement of work (SOW)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="nda">- Non-disclosure agreement (NDA)</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="bpa">- Business partners agreement (BPA)</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="vendor-monitoring">• Vendor monitoring</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="vendor-questionnaires">• Questionnaires</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="rules-of-engagement">• Rules of engagement</ClickableBullet>
            </h4>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-green-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-green-500 text-white px-2 py-1 rounded mr-3 text-sm">5.4</span>
          Summarize elements of effective security compliance.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="compliance">• Compliance reporting</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="internal-compliance">- Internal</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="external-compliance">- External</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="consequences-non-compliance">• Consequences of non-compliance</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="fines">- Fines</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="sanctions">- Sanctions</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="reputational-damage">- Reputational damage</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="loss-of-license">- Loss of license</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="contractual-impacts">- Contractual impacts</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="compliance-monitoring">• Compliance monitoring</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="due-diligence-care">- Due diligence/care</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="attestation">- Attestation and acknowledgement</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="internal-external">- Internal and external</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="automation-compliance">- Automation</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="privacy-compliance">• Privacy</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="legal-implications">- Legal implications</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="local-regional-legal" level={1}>° Local/regional</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="national-legal" level={1}>° National</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="global-legal" level={1}>° Global</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-subject">- Data subject</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="controller-vs-processor">- Controller vs. processor</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="ownership-data">- Ownership</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="data-inventory">- Data inventory and retention</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="right-to-be-forgotten">- Right to be forgotten</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-green-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-green-500 text-white px-2 py-1 rounded mr-3 text-sm">5.5</span>
          Explain types and purposes of audits and assessments.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="attestation-audit">• Attestation</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="internal-audits">• Internal</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="compliance-audit">- Compliance</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="audit-committee">- Audit committee</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="self-assessments">- Self-assessments</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="external-audits">• External</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="regulatory-audit">- Regulatory</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="examinations">- Examinations</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="assessment">- Assessment</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="independent-third-party-audit">- Independent third-party audit</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="penetration-testing-audit">• Penetration testing</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="physical-pen-test">- Physical</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="offensive">- Offensive</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="defensive">- Defensive</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="integrated">- Integrated</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="known-environment">- Known environment</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="partially-known-environment">- Partially known environment</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="unknown-environment">- Unknown environment</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="reconnaissance">- Reconnaissance</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="passive-recon" level={1}>° Passive</ClickableBullet>
              </li>
              <li className="ml-4">
                <ClickableBullet conceptKey="active-recon" level={1}>° Active</ClickableBullet>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-green-500 pl-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-green-500 text-white px-2 py-1 rounded mr-3 text-sm">5.6</span>
          Given a scenario, implement security awareness practices.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="phishing">• Phishing</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="campaigns">- Campaigns</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="recognizing-phishing-attempt">- Recognizing a phishing attempt</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="responding-to-suspicious-messages">- Responding to reported suspicious messages</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="anomalous-behavior-recognition">• Anomalous behavior recognition</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="risky">- Risky</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="unexpected">- Unexpected</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="unintentional">- Unintentional</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="user-guidance-training">• User guidance and training</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="policy-handbooks">- Policy/handbooks</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="situational-awareness">- Situational awareness</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="insider-threat-awareness">- Insider threat</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="password-management">- Password management</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="removable-media-cables">- Removable media and cables</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="social-engineering">- Social engineering</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="operational-security">- Operational security</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="hybrid-remote-work">- Hybrid/remote work environments</ClickableBullet>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="reporting-monitoring">• Reporting and monitoring</ClickableBullet>
            </h4>
            <ul className="ml-4 space-y-1">
              <li>
                <ClickableBullet conceptKey="initial">- Initial</ClickableBullet>
              </li>
              <li>
                <ClickableBullet conceptKey="recurring-training">- Recurring</ClickableBullet>
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-3">
              <ClickableBullet conceptKey="development">• Development</ClickableBullet>
            </h4>
            <h4 className="font-semibold mb-2">
              <ClickableBullet conceptKey="execution">• Execution</ClickableBullet>
            </h4>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDomainContent = (domainId) => {
    switch(domainId) {
      case 1:
        return <Domain1Content />;
      case 2:
        return <Domain2Content />;
      case 3:
        return <Domain3Content />;
      case 4:
        return <Domain4Content />;
      case 5:
        return <Domain5Content />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <Header domains={domains} />

      {/* Search and Progress */}
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showBookmarks={showBookmarks}
        setShowBookmarks={setShowBookmarks}
        practiceMode={practiceMode}
        setPracticeMode={setPracticeMode}
        bookmarkedConcepts={bookmarkedConcepts}
        searchConcepts={searchConcepts}
        getBookmarkedConcepts={getBookmarkedConcepts}
        setSelectedConcept={setSelectedConcept}
        toggleBookmark={toggleBookmark}
      />
      <ProgressTracker />
      <StudyTimer 
        studyTime={studyTime}
        isStudying={isStudying}
        formatTime={formatTime}
        getCurrentStudyTime={getCurrentStudyTime}
        startStudySession={startStudySession}
        pauseStudySession={pauseStudySession}
        resetStudySession={resetStudySession}
      />
      <PracticeMode />

      {/* Domain Navigation */}
      <DomainNavigation />

      {/* Domain Overview or Specific Domain */}
      {selectedDomain === null ? (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Exam Objectives (Domains)</h2>
          <div className="grid gap-6">
            {domains.map((domain) => (
              <DomainCard 
                key={domain.id}
                domain={domain}
                onDomainSelect={setSelectedDomain}
              />
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-6">
            <h2 className={`text-2xl font-bold mb-2 ${domains[selectedDomain - 1].iconColor}`}>
              {domains[selectedDomain - 1].title}
            </h2>
            <div className="flex items-center space-x-4">
              <span className={`inline-block px-3 py-1 rounded-full text-white text-sm font-semibold ${domains[selectedDomain - 1].color}`}>
                {domains[selectedDomain - 1].percentage} of Examination
              </span>
            </div>
          </div>
          {renderDomainContent(selectedDomain)}
        </div>
      )}

      {/* Concept Modal */}
      <ConceptModal 
        selectedConcept={selectedConcept}
        onClose={() => setSelectedConcept(null)}
        bookmarkedConcepts={bookmarkedConcepts}
        toggleBookmark={toggleBookmark}
      />

      {/* Footer */}
      <div className="mt-12 pt-6 border-t text-center text-sm text-gray-500">
        <p>Made By: @Free Will - Humans have the ability to make their own choices and determine their own fates. Aug 22, 2025.</p>
      </div>
      <ScrollToTop />
    </div>
  );
};

export default CompTIASecurityPlusObjectives;